import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePharmacyGoodsReceipt,
  useGeneratePharmacyInvoiceFromGrn,
} from '../../services/httpServices/queries';
import { ArrowLeft, Loader2, ReceiptText, PackageCheck, Wallet, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { PharmacyPurchaseReturnDialog } from './PharmacyPurchaseReturnDialog';

const invoiceStyles: Record<string, string> = {
  UNPAID: 'bg-danger/10 text-danger',
  PARTIAL: 'bg-warning/10 text-warning',
  PAID: 'bg-success/10 text-success',
  OVERDUE: 'bg-danger/10 text-danger',
};

export function PharmacyReceiptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePharmacyGoodsReceipt(id);
  const generateMutation = useGeneratePharmacyInvoiceFromGrn();
  const [showReturn, setShowReturn] = useState(false);

  const receipt = data?.data;

  if (isLoading) {
    return (
      <PharmacyLayout title="Goods Receipt">
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        </div>
      </PharmacyLayout>
    );
  }

  if (error || !receipt) {
    return (
      <PharmacyLayout title="Goods Receipt">
        <div className="py-20 text-center text-danger">Goods receipt not found.</div>
      </PharmacyLayout>
    );
  }

  const invoice = receipt.invoice;
  const total = Number(receipt.totalAmount || 0);
  const balance = invoice
    ? Number(invoice.amount) - Number(invoice.paidAmount) - Number(invoice.creditedAmount ?? 0)
    : 0;
  const canReturn = (receipt.items ?? []).some(
    (item) => Number(item.quantityReceived) - Number(item.returnedQuantity ?? 0) > 0,
  );
  const returns = receipt.returns ?? [];

  const handleGenerate = async () => {
    try {
      const res = await generateMutation.mutateAsync({ grnId: receipt.id });
      toast.success(`Invoice ${res.data?.invoiceNumber || ''} generated`);
    } catch {
      // handled globally
    }
  };

  return (
    <PharmacyLayout
      title={receipt.grnNumber}
      description={`Goods received from ${receipt.supplier?.name || 'supplier'}`}
      action={
        <div className="flex items-center gap-2">
          {canReturn && (
            <button
              onClick={() => setShowReturn(true)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium text-text hover:bg-background flex items-center gap-1.5"
            >
              <RotateCcw size={15} /> Return to Supplier
            </button>
          )}
          {invoice ? (
            <button
              onClick={() => navigate('/pharmacy/payables')}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium text-text hover:bg-background flex items-center gap-1.5"
            >
              <Wallet size={15} /> View Payables
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
              className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark flex items-center gap-1.5 disabled:opacity-50"
            >
              {generateMutation.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <ReceiptText size={15} />
              )}
              Generate Supplier Bill
            </button>
          )}
        </div>
      }
    >
      <button
        onClick={() => navigate('/pharmacy/receipts')}
        className="text-sm text-text-muted hover:text-text flex items-center gap-1.5"
      >
        <ArrowLeft size={16} /> Back to goods receipts
      </button>

      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="text-xs text-text-muted uppercase">Supplier</div>
          <div className="text-sm font-medium text-text">{receipt.supplier?.name || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Received Date</div>
          <div className="text-sm font-medium text-text">
            {receipt.receivedDate ? new Date(receipt.receivedDate).toLocaleDateString() : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Purchase Order</div>
          {receipt.po ? (
            <button
              onClick={() => navigate(`/pharmacy/purchases/${receipt.poId}`)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {receipt.po.poNumber}
            </button>
          ) : (
            <div className="text-sm font-medium text-text">Direct purchase</div>
          )}
        </div>
        <div>
          <div className="text-xs text-text-muted uppercase">Supplier Invoice No.</div>
          <div className="text-sm font-medium text-text">{receipt.invoiceNumber || '—'}</div>
        </div>
      </div>

      {invoice ? (
        <div className="bg-surface rounded-xl border border-success/30 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <PackageCheck className="w-5 h-5 text-success" />
            </div>
            <div>
              <div className="text-sm font-medium text-text">Invoice {invoice.invoiceNumber}</div>
              <div className="text-xs text-text-muted">
                Due {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'} • Balance{' '}
                {balance.toFixed(2)}
                {Number(invoice.creditedAmount ?? 0) > 0 && (
                  <> • Credited {Number(invoice.creditedAmount).toFixed(2)}</>
                )}
              </div>
            </div>
          </div>
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              invoiceStyles[invoice.status] || 'bg-background text-text-muted border border-border'
            }`}
          >
            {invoice.status}
          </span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-warning/30 p-4 shadow-sm flex items-center gap-3">
          <div className="p-2 bg-warning/10 rounded-lg">
            <ReceiptText className="w-5 h-5 text-warning" />
          </div>
          <div>
            <div className="text-sm font-medium text-text">No supplier bill yet</div>
            <div className="text-xs text-text-muted">
              Generate a payable from this receipt once the supplier invoice is confirmed.
            </div>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-background border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Drug</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Qty Received</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Returned</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Unit Cost</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Batch</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Expiry</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Line Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(receipt.items || []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-text">{item.drug?.name || item.drugId}</td>
                <td className="px-4 py-3 text-right text-text">{Number(item.quantityReceived)}</td>
                <td className="px-4 py-3 text-right text-text-muted">
                  {Number(item.returnedQuantity ?? 0) > 0 ? Number(item.returnedQuantity) : '—'}
                </td>
                <td className="px-4 py-3 text-right text-text-muted">{Number(item.unitCost).toFixed(2)}</td>
                <td className="px-4 py-3 font-mono text-text-muted">{item.batchNumber || '—'}</td>
                <td className="px-4 py-3 text-text-muted">
                  {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right font-medium text-text">
                  {(Number(item.quantityReceived) * Number(item.unitCost)).toFixed(2)}
                </td>
              </tr>
            ))}
            {(receipt.items || []).length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-text-muted">
                  No items.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {returns.length > 0 && (
        <div className="bg-surface rounded-xl border border-border shadow-sm">
          <div className="flex items-center gap-2 p-4 border-b border-border">
            <RotateCcw size={16} className="text-primary" />
            <span className="font-medium text-text">Returns to Supplier</span>
            <span className="text-xs text-text-muted">({returns.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Return</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Reason</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Value</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Credited</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {returns.map((ret) => (
                  <tr key={ret.id}>
                    <td className="px-4 py-3 font-mono text-text">{ret.returnNumber}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {ret.returnedAt ? new Date(ret.returnedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{ret.reason || '—'}</td>
                    <td className="px-4 py-3 text-right text-text font-medium">
                      {Number(ret.totalAmount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-text-muted">
                      {Number(ret.creditedAmount).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm flex justify-end">
        <div className="w-64 space-y-1 text-sm">
          <div className="flex justify-between pt-2 border-t border-border font-bold">
            <span className="text-text">Receipt Total</span>
            <span className="text-primary">{total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {showReturn && (
        <PharmacyPurchaseReturnDialog receipt={receipt} onClose={() => setShowReturn(false)} />
      )}
    </PharmacyLayout>
  );
}
